import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { Clock, Save, Swords } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { DatePicker } from "@/shared/components/DatePicker";
import {
  FormDialog,
  submitOnEnter,
  useCloseOrError,
} from "@/shared/components/FormDialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/components/ui/alert-dialog";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/shared/components/ui/form";
import { Input } from "@/shared/components/ui/input";
import { Textarea } from "@/shared/components/ui/textarea";
import type { components } from "@/shared/lib/api.types";
import { useResetOnOpen } from "@/shared/lib/useResetOnOpen";
import { cn } from "@/shared/lib/utils";
import { todayIso, toHourMinute, weekdayIndex } from "@/shared/utils/date";
import {
  useCreateRaid,
  useDeleteRaid,
  useUpdateRaid,
} from "./api/raid-day.mutations";
import { raidContentQueryOptions } from "./api/raid-day.queries";
import {
  type RaidFormInput,
  type RaidFormValues,
  raidSchema,
} from "./raid-day.schemas";
import { reconcileSelectedIds } from "./raidContent";

type RaidContentOption = components["schemas"]["RaidContentOption"];

export type RaidDialogRaid = {
  id: string;
  date: string;
  startTime: string;
  title: string;
  description: string | null;
  configuredRaidIds: string[];
};

type Confirmation =
  | { kind: "save"; values: RaidFormValues }
  | { kind: "delete" };

function defaultStartTime(dateIso: string): string {
  return weekdayIndex(dateIso) === 0 ? "18:00" : "18:30";
}

function seedValues(date: string, raid?: RaidDialogRaid): RaidFormInput {
  return {
    date: raid?.date ?? date,
    title: raid?.title ?? "",
    startTime: raid?.startTime
      ? toHourMinute(raid.startTime)
      : defaultStartTime(date),
    description: raid?.description ?? "",
    configuredRaidIds: raid?.configuredRaidIds ?? [],
  };
}

function sameIds(left: string[], right: string[]) {
  return (
    left.length === right.length &&
    left.every((id, index) => id === right[index])
  );
}

export function RaidDialog(props: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  date: string;
  raid?: RaidDialogRaid;
}) {
  const { open, onOpenChange, date, raid } = props;
  const mode = raid ? "edit" : "create";
  const requiresConfiguredRaidRef = useRef(false);

  const form = useForm<RaidFormInput, unknown, RaidFormValues>({
    resolver: (values, context, options) =>
      zodResolver(raidSchema(requiresConfiguredRaidRef.current))(
        values,
        context,
        options,
      ),
    defaultValues: seedValues(date, raid),
  });

  useResetOnOpen(open, () => form.reset(seedValues(date, raid)));

  const watchedDate = useWatch({ control: form.control, name: "date" });
  const validDate = /^\d{4}-\d{2}-\d{2}$/.test(watchedDate);
  const queryRaidId = raid && watchedDate === raid.date ? raid.id : undefined;
  const contentQuery = useQuery({
    ...raidContentQueryOptions(watchedDate, queryRaidId),
    enabled: open && validDate,
    throwOnError: true,
  });
  const options = contentQuery.data?.options ?? [];
  const eligibleOptions = options.filter((option) => option.isEligible);
  const hasLegacySelections = Boolean(
    raid &&
      watchedDate === raid.date &&
      options.some((option) => option.isSelected && !option.isEligible),
  );
  const displayedOptions = hasLegacySelections
    ? options.filter((option) => option.isSelected)
    : eligibleOptions;
  const showContentChooser = hasLegacySelections || displayedOptions.length > 0;
  requiresConfiguredRaidRef.current =
    !hasLegacySelections && eligibleOptions.length > 1;
  // A sole eligible option is auto-selected and mandatory — show it, but lock it
  // so it can't be de-selected (there is nothing else to pick).
  const lockSingleOption = !hasLegacySelections && eligibleOptions.length === 1;

  const synchronizedQueryRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open) synchronizedQueryRef.current = null;
  }, [open]);
  useEffect(() => {
    if (!open || !contentQuery.data || contentQuery.data.date !== watchedDate) {
      return;
    }

    const synchronizationKey = `${watchedDate}:${queryRaidId ?? "new"}`;
    const firstSynchronization =
      synchronizedQueryRef.current !== synchronizationKey;
    synchronizedQueryRef.current = synchronizationKey;

    const currentIds = form.getValues("configuredRaidIds");
    const next = reconcileSelectedIds({
      firstSynchronization,
      priorConfiguredIds:
        watchedDate === raid?.date ? (raid?.configuredRaidIds ?? []) : [],
      currentIds,
      hasLegacySelections,
      options,
      eligibleOptions,
    });

    if (!sameIds(currentIds, next)) {
      form.setValue("configuredRaidIds", next, {
        shouldValidate: form.formState.isSubmitted,
      });
    }
  }, [
    contentQuery.data,
    eligibleOptions,
    form,
    hasLegacySelections,
    open,
    options,
    queryRaidId,
    raid,
    watchedDate,
  ]);

  const create = useCreateRaid();
  const update = useUpdateRaid();
  const del = useDeleteRaid();
  const closeOrError = useCloseOrError(onOpenChange);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);

  const executeSubmit = (values: RaidFormValues) => {
    if (mode === "create") {
      create.mutate(values, closeOrError("Kunde inte skapa raiddag"));
    } else if (raid) {
      update.mutate(
        { id: raid.id, ...values },
        closeOrError("Kunde inte uppdatera raiddag"),
      );
    }
  };

  const onSubmit = (values: RaidFormValues) => {
    const removesConfiguredRaid = raid?.configuredRaidIds.some(
      (id) => !values.configuredRaidIds.includes(id),
    );
    const reschedulesConfiguredRaid = Boolean(
      raid && values.date !== raid.date && raid.configuredRaidIds.length > 0,
    );
    if (removesConfiguredRaid || reschedulesConfiguredRaid) {
      setConfirmation({ kind: "save", values });
      return;
    }
    executeSubmit(values);
  };

  const confirm = () => {
    if (confirmation?.kind === "save") {
      executeSubmit(confirmation.values);
    } else if (confirmation?.kind === "delete" && raid) {
      del.mutate(raid.id, closeOrError("Kunde inte ta bort raiddag"));
    }
    setConfirmation(null);
  };

  const mutationPending = create.isPending || update.isPending || del.isPending;
  const contentPending = contentQuery.isFetching;

  return (
    <>
      <FormDialog
        open={open}
        onOpenChange={onOpenChange}
        title={mode === "create" ? "Skapa raid" : "Redigera raid"}
        form={form}
        onSubmit={onSubmit}
        submitting={mutationPending || contentPending}
        submitLabel={mode === "create" ? "Skapa" : "Spara"}
        submitIcon={mode === "create" ? <Swords /> : <Save />}
        onDelete={raid ? () => setConfirmation({ kind: "delete" }) : undefined}
        deleting={del.isPending}
      >
        <div className="flex items-start gap-3">
          <FormField
            control={form.control}
            name="date"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Datum</FormLabel>
                <DatePicker
                  value={field.value}
                  onChange={field.onChange}
                  disabledBefore={todayIso()}
                />
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="startTime"
            render={({ field }) => (
              <FormItem className="w-32">
                <FormLabel>Tid</FormLabel>
                <div className="relative">
                  {/* Firefox renders its own clock button that CSS can't hide, so drop ours there. */}
                  <Clock className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground supports-[-moz-appearance:none]:hidden" />
                  <FormControl>
                    <Input
                      type="time"
                      className="appearance-none pl-9 supports-[-moz-appearance:none]:pl-3 [&::-webkit-calendar-picker-indicator]:hidden"
                      {...field}
                    />
                  </FormControl>
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
        <FormField
          control={form.control}
          name="title"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Titel</FormLabel>
              <FormControl>
                <Input maxLength={100} {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {showContentChooser && (
          <FormField
            control={form.control}
            name="configuredRaidIds"
            render={({ field }) => (
              <FormItem>
                <fieldset
                  aria-label="Raidinnehåll"
                  className="space-y-2 rounded-md border p-3"
                >
                  {displayedOptions.map((option: RaidContentOption) => {
                    const checked = field.value.includes(option.id);
                    const locked = lockSingleOption && option.isEligible;
                    return (
                      <label
                        key={option.id}
                        className={cn(
                          "flex items-center gap-3 rounded-sm px-2 py-1.5 text-sm",
                          locked ? "cursor-not-allowed" : "cursor-pointer",
                          checked && "bg-accent",
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={locked}
                          className="size-4 accent-primary disabled:opacity-60"
                          onChange={(event) =>
                            field.onChange(
                              event.target.checked
                                ? [...field.value, option.id]
                                : field.value.filter((id) => id !== option.id),
                            )
                          }
                        />
                        <span className="flex-1">{option.name}</span>
                        <span className="text-xs text-muted-foreground">
                          {Number(option.bossCount)} bossar
                        </span>
                      </label>
                    );
                  })}
                </fieldset>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="description"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Beskrivning</FormLabel>
              <FormControl>
                <Textarea
                  maxLength={500}
                  className="resize-none"
                  {...field}
                  onKeyDown={submitOnEnter(form.handleSubmit(onSubmit))}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </FormDialog>

      <AlertDialog
        open={confirmation !== null}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) setConfirmation(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirmation?.kind === "delete"
                ? "Ta bort raiddag?"
                : "Ändra raidinnehåll?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirmation?.kind === "delete"
                ? "Raiddagen och all setup- och bossdata för dagen tas bort. Detta går inte att ångra."
                : "Ändringen kan ta bort bossval och tilldelningar som inte längre passar datumet eller de valda raiderna. Detta går inte att ångra."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={mutationPending}>
              Avbryt
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={mutationPending}
              onClick={confirm}
            >
              {confirmation?.kind === "delete" ? "Ta bort" : "Fortsätt"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
